import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { apiGetJson, apiPutJson } from "../utils/apiClient";
import LoadingInline from "../components/LoadingInline";
import { useUserContext } from "../contexts/UserContext";
import { isFreePlanUser } from "../utils/plan";

// ✅ 12ヶ月版と同じ「税込」へ統一
const SHOP_PLANS = [
    { label: "がんばれ", fee: 27500 },
    { label: "スタンダード", fee: 71500 },
    { label: "メガショップ", fee: 143000 },
];

// ★ apiClient と同じ判定（exportされてないのでページ側に最小実装）
function isDemoModeLocal(): boolean {
    if (typeof window === "undefined") return false;
    if (window.location.pathname.startsWith("/demo")) return true;
    const ls = localStorage.getItem("demoMode");
    return ls === "1" || ls === "true";
}

// ✅ 12ヶ月版の項目に合わせたラベル（機能は従来通り：テキスト＆数値入力）
const SYSTEM_ROWS = [
    // 固定サービス 3つ（paid_system_1〜3）
    { nameField: "rcabinet_plan", costField: "rcabinet_cost", label: "R-Cabinet（容量追加など）" },
    { nameField: "rakuraku_coupon_plan", costField: "rakuraku_coupon_cost", label: "らくらくーぽん" },
    { nameField: "ecmasters_plan", costField: "ecmasters_cost", label: "ECマスターズクラブ" },

    // 自由入力 5つ（paid_system_4〜8）
    { nameField: "paid_system_1_name", costField: "paid_system_1_cost", label: "自由入力①", placeholder: "" },
    { nameField: "paid_system_2_name", costField: "paid_system_2_cost", label: "自由入力②", placeholder: "" },
    { nameField: "paid_system_3_name", costField: "paid_system_3_cost", label: "自由入力③", placeholder: "" },
    { nameField: "paid_system_4_name", costField: "paid_system_4_cost", label: "自由入力④", placeholder: "" },
    { nameField: "paid_system_5_name", costField: "paid_system_5_cost", label: "自由入力⑤", placeholder: "" },
    { nameField: "paid_system_6_name", costField: "paid_system_6_cost", label: "自由入力⑥", placeholder: "" },
    { nameField: "paid_system_7_name", costField: "paid_system_7_cost", label: "自由入力⑦", placeholder: "" },
] as const;

export default function MonthlyRakutenExpensePage({ shopUrl }: { shopUrl: string }) {
    const user = useUserContext();

    const [planBlockMsg, setPlanBlockMsg] = useState<string | null>(null);
    const planBlockTimer = React.useRef<number | null>(null);

    const isDemo = useMemo(() => isDemoModeLocal(), []);

    // ✅ demoでshopUrlが渡ってない時の保険（fixtureもこれで作る）
    const shopUrlSafe = useMemo(() => {
        if (shopUrl && shopUrl.trim()) return shopUrl.trim();
        return isDemo ? "demo-shop" : shopUrl;
    }, [shopUrl, isDemo]);

    const isFreePlan = useMemo(() => isFreePlanUser(user, shopUrl), [user, shopUrl]);

    const [yearMonth, setYearMonth] = useState(dayjs().format("YYYY-MM"));
    const [form, setForm] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const savingRef = React.useRef(false);

    const ymDate = `${yearMonth}-01`;

    const update = (field: string, value: any) => {
        setForm((prev: any) => ({ ...prev, [field]: value }));
    };

    const updateShopFee = (planLabel: string) => {
        const plan = SHOP_PLANS.find((p) => p.label === planLabel);
        update("shop_plan", planLabel);
        update("shop_fee", plan ? plan.fee : null);
    };

    // ✅ 12ヶ月版の項目に寄せて「8本」持つ
    const emptyForm = {
        courier_shipping_cost: null,
        mail_shipping_cost: null,
        logistics_cost: null,

        shop_plan: "",
        shop_fee: null,

        rsns: null,
        rms_batch_edit: null,
        line_plan_cost: null,

        paid_system_1_name: "",
        paid_system_1_cost: null,
        paid_system_2_name: "",
        paid_system_2_cost: null,
        paid_system_3_name: "",
        paid_system_3_cost: null,

        paid_system_4_name: "",
        paid_system_4_cost: null,
        paid_system_5_name: "",
        paid_system_5_cost: null,
        paid_system_6_name: "",
        paid_system_6_cost: null,
        paid_system_7_name: "",
        paid_system_7_cost: null,

        other_fixed_cost: null,
    };

    const fixedTotal = (() => {
        if (!form) return 0;

        const fields = [
            "shop_fee",
            "rsns",
            "rms_batch_edit",
            "line_plan_cost",

            "paid_system_1_cost",
            "paid_system_2_cost",
            "paid_system_3_cost",
            "paid_system_4_cost",
            "paid_system_5_cost",
            "paid_system_6_cost",
            "paid_system_7_cost",

            "other_fixed_cost",
            "logistics_cost",
            "courier_shipping_cost",
            "mail_shipping_cost",
        ];

        return fields.reduce((sum, key) => {
            const v = Number(form[key]);
            return sum + (isNaN(v) ? 0 : v);
        }, 0);
    })();

    const load = async () => {
        setLoading(true);
        setError(null);

        try {
            const data = await apiGetJson<any>(
                `/api/rakuten-expense?shop_url=${encodeURIComponent(shopUrlSafe)}&year_month=${yearMonth}`
            );

            if (data) setForm({ ...emptyForm, ...data });
            else setForm(emptyForm);
        } catch (e: any) {
            console.warn("[MonthlyRakutenExpensePage] load failed:", e);
            setError(e?.message || "load error");
            setForm(emptyForm);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [yearMonth, shopUrlSafe]);

    const handleSave = async () => {
        if (!form) return;
        if (savingRef.current) return;

        savingRef.current = true;
        setSaving(true);

        try {
            await apiPutJson("/api/rakuten-expense", {
                shop_url: shopUrlSafe,
                year_month: ymDate,
                ...form,
            });

            alert("保存しました");
            load();
        } catch (e: any) {
            console.warn("[MonthlyRakutenExpensePage] save failed:", e);
            alert(e?.message || "保存に失敗しました");
        } finally {
            savingRef.current = false;
            setSaving(false);
        }
    };

    // ★★★ 先月コピー ★★★
    const handleCopyPrev = async () => {
        const prevYm = dayjs(yearMonth).subtract(1, "month").format("YYYY-MM");

        let prevData: any = null;
        try {
            prevData = await apiGetJson<any>(
                `/api/rakuten-expense?shop_url=${encodeURIComponent(shopUrlSafe)}&year_month=${prevYm}`
            );
        } catch (e: any) {
            console.warn("[MonthlyRakutenExpensePage] copy-prev load failed:", e);
            prevData = null;
        }

        if (!prevData) {
            alert("先月のデータがありません");
            return;
        }

        // shop_fee はプランから計算しているため整合性取る
        let shop_fee = null;
        const plan = SHOP_PLANS.find((p) => p.label === prevData.shop_plan);
        if (plan) shop_fee = plan.fee;

        setForm({
            ...emptyForm,
            ...prevData,
            shop_fee,
        });

        alert(`${dayjs(prevYm).format("YYYY年MM月")}の内容をコピーしました`);
    };

    if (!form) return <LoadingInline />;

    return (
        <div className="p-6">
            <div className="max-w-3xl">
                {/* ▼ 上部ナビゲーション */}
                <div className="flex justify-between items-center mb-6">
                    {/* 前月/次月 */}
                    <div className="flex items-center">
                        <button
                            className="px-3 py-1.5 rounded border bg-white hover:bg-gray-50"
                            onClick={() => setYearMonth(dayjs(yearMonth).subtract(1, "month").format("YYYY-MM"))}
                        >
                            ← 前月
                        </button>

                        <div className="text-lg font-semibold w-40 text-center">
                            {dayjs(yearMonth).format("YYYY年MM月")}
                        </div>

                        <button
                            className="px-3 py-1.5 rounded border bg-white hover:bg-gray-50"
                            onClick={() => setYearMonth(dayjs(yearMonth).add(1, "month").format("YYYY-MM"))}
                        >
                            次月 →
                        </button>
                    </div>

                    {/* 先月コピー + 保存 */}
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleCopyPrev}
                            className="px-3 py-1.5 border bg-white rounded hover:bg-gray-50"
                            disabled={loading}
                        >
                            先月からコピー
                        </button>

                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className={`px-4 py-2 bg-blue-600 text-white rounded shadow-sm hover:bg-blue-700 ${saving ? "opacity-60 cursor-not-allowed" : ""
                                }`}
                        >
                            {saving ? "保存中..." : "保存"}
                        </button>
                    </div>
                </div>

                {planBlockMsg && (
                    <div className="mb-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                        {planBlockMsg}
                    </div>
                )}

                {error && <div className="mb-3 text-sm text-red-600">{error}</div>}

                {/* 固定費合計 */}
                <div className="flex justify-end mt-4">
                    <div className="inline-block p-3 bg-gray-100 rounded border font-bold text-base text-right">
                        固定費合計: {fixedTotal.toLocaleString()} 円
                    </div>
                </div>

                {/* ▼ 入力フォーム */}
                <div className="grid grid-cols-2 gap-6 mt-6">
                    {/* 出店プラン */}
                    <div>
                        <label className="text-sm text-gray-600">出店プラン</label>
                        <select
                            className="w-full border px-2 py-2 mt-1"
                            value={form.shop_plan ?? ""}
                            onChange={(e) => updateShopFee(e.target.value)}
                        >
                            <option value="">選択してください</option>
                            {SHOP_PLANS.map((p) => (
                                <option key={p.label} value={p.label}>
                                    {p.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* 出店料 */}
                    <div>
                        <label className="text-sm text-gray-600">出店料</label>
                        <input
                            type="number"
                            className="w-full border px-2 py-2 mt-1 bg-gray-100 text-right"
                            value={form.shop_fee ?? ""}
                            disabled
                        />
                    </div>

                    {/* 数値系フィールド */}
                    {[
                        ...(isFreePlan
                            ? []
                            : ([
                                ["courier_shipping_cost", "宅急便送料（税込）／1配送辺りの平均金額"],
                                ["mail_shipping_cost", "メール便送料（税込）／1配送辺りの平均金額"],
                            ] as Array<[string, string]>)),
                        ["logistics_cost", isFreePlan ? "平均物流費（税込）" : "物流原価（税込）"],
                        ["rsns", "R-SNS"],
                        ["rms_batch_edit", "RMS商品一括編集機能"],
                        ["line_plan_cost", "LINEプラン費用"],
                        ["other_fixed_cost", "その他（合算）"],
                    ].map(([field, label]) => (
                        <div key={field}>
                            <label className="text-sm text-gray-600">{label}</label>
                            <input
                                type="number"
                                className="w-full border px-2 py-2 mt-1 text-right"
                                value={(form as any)[field] ?? ""}
                                onChange={(e) =>
                                    update(field, e.target.value === "" ? null : Number(e.target.value))
                                }
                            />
                        </div>
                    ))}

                    {/* ✅ 固定サービス＋自由入力（項目合わせ） */}
                    {SYSTEM_ROWS.map((row) => (
                        <React.Fragment key={row.nameField}>
                            <div>
                                <label className="text-sm text-gray-600">{row.label} 名称</label>
                                <input
                                    type="text"
                                    className="w-full border px-2 py-2 mt-1"
                                    placeholder={(row as any).placeholder || ""}
                                    value={(form as any)[row.nameField] ?? ""}
                                    onChange={(e) => update(row.nameField, e.target.value)}
                                />
                            </div>

                            <div>
                                <label className="text-sm text-gray-600">{row.label} 費用</label>
                                <input
                                    type="number"
                                    className="w-full border px-2 py-2 mt-1 text-right"
                                    value={(form as any)[row.costField] ?? ""}
                                    onChange={(e) =>
                                        update(row.costField, e.target.value === "" ? null : Number(e.target.value))
                                    }
                                />
                            </div>
                        </React.Fragment>
                    ))}
                </div>
            </div>
        </div>
    );
}
